import { useMemo } from 'react';
import { LANGUAGES } from '@constants/translations';
import { resolvePatchLogCopy } from './i18n';
import { isPatchLogLocale, type PatchLogLocale } from './types';

/** The receipt column has no room for "Bahasa Indonesia"; the full name stays as the button's title. */
const LOCALE_SHORT_LABEL: Record<PatchLogLocale, string> = { id: 'ID', en: 'EN', zh: '中文' };

interface PatchLogLocaleSwitcherProps {
  locale: PatchLogLocale;
  onChange: (locale: PatchLogLocale) => void;
  /** Extra class on the group, e.g. to grow the hit targets on the history page. */
  className?: string;
}

/**
 * Quiet segmented control for the patch-log locale, shared by the blocking
 * modal's masthead and the history page header (Issue 8 wants one in both).
 *
 * Options come from the app's language registry, so a new app locale appears
 * here automatically once the patch-log bundle supports it. Renders nothing
 * when there is only one locale to choose from.
 */
export const PatchLogLocaleSwitcher = ({ locale, onChange, className }: PatchLogLocaleSwitcherProps) => {
  const options = useMemo(
    () =>
      LANGUAGES.filter((meta) => isPatchLogLocale(meta.code)).map((meta) => ({
        value: meta.code as PatchLogLocale,
        label: meta.label,
      })),
    []
  );

  if (options.length < 2) {
    return null;
  }

  return (
    <div
      className={className ? `jarvis-patch-log__lang ${className}` : 'jarvis-patch-log__lang'}
      role="group"
      aria-label={resolvePatchLogCopy(locale, 'language')}
    >
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          className="jarvis-patch-log__lang-option"
          aria-pressed={option.value === locale}
          title={option.label}
          onClick={() => onChange(option.value)}
        >
          {LOCALE_SHORT_LABEL[option.value] ?? option.value.toUpperCase()}
        </button>
      ))}
    </div>
  );
};

export default PatchLogLocaleSwitcher;
